"use client"

import { useEffect, useState, useRef } from "react"
import { useTranslation } from 'react-i18next'
import VehicleCard from "./VehicleCard"
import NotificationModal from "./modals/NotificationModal"
import QuickActionPanel from "./QuickActionPanel"
// Import Swiper and required modules
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation, A11y } from 'swiper/modules'
import { ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'

const VehiclesSection = ({
  cars,
  setShowAddCarModal,
  selectedCar,
  setSelectedCar,
  isCardClicked,
  setIsCardClicked,
  onCarsChanged,
}) => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const paginationRef = useRef(null) // Create a ref for the pagination container

  // Handle slide change
  const handleSlideChange = (swiper) => {
    const newIndex = swiper.realIndex;
    setActiveIndex(newIndex);
    if (newIndex < cars.length) {
      setSelectedCar(cars[newIndex]);
      setIsCardClicked(true);
    } else {
      // This is the "Add Car" slide
      // Keep the selection but mark that we're on the add car slide
      setIsCardClicked(false);
    }
  };

  // Handle card click
  const handleCardClick = (car, index) => {
    setSelectedCar(car)
    setIsCardClicked(true)
    setActiveIndex(index)
  }

  // Set initial selected car if none is selected yet
  useEffect(() => {
    if (cars.length > 0) {
      // Always set the first car as active on initial load
      setSelectedCar(cars[0]);
      setActiveIndex(0);
      setIsCardClicked(true); // Set to true to show details immediately
    }
    // Removed the dependency array check for !selectedCar to ensure it always runs on cars change if needed
  }, [cars, setSelectedCar, setActiveIndex, setIsCardClicked]); // Keep dependencies

  // Helper: format date
  const formatDate = (dateString) => {
    if (!dateString) return t('vehicleStatus.notSet');
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('ro-RO', options);
    } catch {
      return dateString;
    }
  };

  // Helper: get status for ITP, RCA, CASCO
  const getExpiryStatus = (date) => {
    if (!date) return { expired: false, nearing: false };
    const today = new Date();
    const expiryDate = new Date(date);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      expired: diffDays < 0,
      nearing: diffDays >= 0 && diffDays <= 30,
    };
  };

  return (
    <div className="mb-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        {/* <h2 className="text-xl font-semibold dark:text-white">{t('vehicles.yourCars')}</h2> */}
      </div>

      {/* Swiper Carousel */}
      <div className="relative">
        <Swiper
          modules={[Pagination, Navigation, A11y]}
          spaceBetween={24}
          slidesPerView={'auto'}
          centeredSlides={true} // Always center slides
          initialSlide={0}
          pagination={{
            el: '.swiper-pagination-custom', // Use custom element for pagination
            clickable: true,
          }}
          onSlideChange={handleSlideChange}
          className="pb-2" // Reduce padding as pagination is now outside
        >
          {cars.map((car, index) => (
            <SwiperSlide
              key={car.id}
              style={{ width: '300px' }}
            >
              <div className="py-2">
                <VehicleCard
                  car={car}
                  isSelected={activeIndex === index}
                  onClick={() => handleCardClick(car, index)}
                />
              </div>
            </SwiperSlide>
          ))}
          {/* Add Car Slide */}
          <SwiperSlide style={{ width: '300px' }}>
            <div className="py-2 h-full">
              <VehicleCard
                isAddButton={true}
                isSelected={activeIndex === cars.length}
                onClick={() => {
                  setShowAddCarModal(true);
                }}
              />
            </div>
          </SwiperSlide>
        </Swiper>

        {/* Custom pagination container outside the Swiper */}
        <div
          className="swiper-pagination-custom flex justify-center"
          ref={paginationRef}
        ></div>
      </div>

      {/* QuickActionPanel - show when a car is selected */}
      {isCardClicked && selectedCar && (
        <div className="mt-6">
          <QuickActionPanel
            selectedCar={selectedCar}
            onCarsChanged={onCarsChanged}
          />
        </div>
      )}

      {/* Car details section - only show if a car was explicitly clicked */}
      {isCardClicked && selectedCar && (
        <div className="animate-fadeIn">
          {/* Header with notification button */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* ITP Card */}
            {(() => {
              const status = getExpiryStatus(selectedCar.ITP);
              const isNotSet = !selectedCar.ITP;
              const baseClasses = "p-4 rounded-lg shadow-md flex flex-col justify-between";
              const colorClasses = status.expired
                ? 'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700/30'
                : status.nearing
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30'
                  : 'bg-white dark:bg-slate-800';
              return (
                <div className={`${baseClasses} ${colorClasses}`}>
                  <div className="flex items-center mb-2">
                    <ClockIcon className={`h-5 w-5 mr-2 ${status.expired ? 'text-rose-500' : status.nearing ? 'text-amber-500' : 'text-slate-400'}`} />
                    <h4 className="font-semibold text-slate-900 dark:text-white">ITP</h4>
                  </div>
                  {isNotSet ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(selectedCar.ITP)}</p>
                      <button
                        onClick={() => setShowNotificationModal(true)}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-md transition-colors"
                      >
                        {t('vehicleStatus.setButton')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">{formatDate(selectedCar.ITP)}</p>
                      {(status.expired || status.nearing) && (
                        <p className={`text-xs font-bold mt-1 ${status.expired ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {status.expired ? t('vehicles.status.expired') : t('vehicles.status.expiringSoon')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* RCA Card */}
            {(() => {
              const status = getExpiryStatus(selectedCar.RCA);
              const isNotSet = !selectedCar.RCA;
              const baseClasses = "p-4 rounded-lg shadow-md flex flex-col justify-between";
              const colorClasses = status.expired
                ? 'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700/30'
                : status.nearing
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30'
                  : 'bg-white dark:bg-slate-800';
              return (
                <div className={`${baseClasses} ${colorClasses}`}>
                  <div className="flex items-center mb-2">
                    <DocumentTextIcon className={`h-5 w-5 mr-2 ${status.expired ? 'text-rose-500' : status.nearing ? 'text-amber-500' : 'text-slate-400'}`} />
                    <h4 className="font-semibold text-slate-900 dark:text-white">RCA</h4>
                  </div>
                  {isNotSet ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(selectedCar.RCA)}</p>
                      <button
                        disabled
                        className="px-3 py-1.5 bg-slate-400 text-white text-sm font-medium rounded-md cursor-not-allowed opacity-60"
                      >
                        {t('vehicleStatus.comingSoon')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">{formatDate(selectedCar.RCA)}</p>
                      {(status.expired || status.nearing) && (
                        <p className={`text-xs font-bold mt-1 ${status.expired ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {status.expired ? t('vehicles.status.expired') : t('vehicles.status.expiringSoon')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* CASCO Card */}
            {(() => {
              const status = getExpiryStatus(selectedCar.CASCO);
              const isNotSet = !selectedCar.CASCO;
              const baseClasses = "p-4 rounded-lg shadow-md flex flex-col justify-between";
              const colorClasses = status.expired
                ? 'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700/30'
                : status.nearing
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30'
                  : 'bg-white dark:bg-slate-800';
              return (
                <div className={`${baseClasses} ${colorClasses}`}>
                  <div className="flex items-center mb-2">
                    <DocumentTextIcon className={`h-5 w-5 mr-2 ${status.expired ? 'text-rose-500' : status.nearing ? 'text-amber-500' : 'text-slate-400'}`} />
                    <h4 className="font-semibold text-slate-900 dark:text-white">CASCO</h4>
                  </div>
                  {isNotSet ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(selectedCar.CASCO)}</p>
                      <button
                        disabled
                        className="px-3 py-1.5 bg-slate-400 text-white text-sm font-medium rounded-md cursor-not-allowed opacity-60"
                      >
                        {t('vehicleStatus.comingSoon')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">{formatDate(selectedCar.CASCO)}</p>
                      {(status.expired || status.nearing) && (
                        <p className={`text-xs font-bold mt-1 ${status.expired ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {status.expired ? t('vehicles.status.expired') : t('vehicles.status.expiringSoon')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        selectedCar={selectedCar}
      />

      {/* Add some CSS for proper alignment */}
      <style jsx="true">{`
        @media (min-width: 768px) {
          .swiper-wrapper {
            justify-content: flex-start !important;
          }
          
          /* Add left padding to container on desktop for better alignment */
          .swiper {
            padding-left: 4px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default VehiclesSection

